import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import View from '@sellermodules/saleschannel/pages/add/components/Modal/view';
import gqlService from '@sellermodules/saleschannel/services/graphql';

const connectorField = [
    {
        // description: 'Domain',
        name: 'domain',
        is_required: true,
        required: true,
        type: 'string',
    },
    {
        // description: 'Shop Name',
        name: 'shop_name',
        is_required: true,
        required: true,
        type: 'string',
    },
    {
        // description: 'Token',
        name: 'token',
        is_required: true,
        required: true,
        type: 'string',
    },
    {
        // description: 'Client Key',
        name: 'client_key',
        is_required: true,
        required: true,
        type: 'string',
    },
    {
        // description: 'Client Secret',
        name: 'client_secret',
        is_required: true,
        required: true,
        type: 'string',
    },
];
const Core = (props) => {
    const {
        t, dataMp, openDialog, setOpenDialog, setSuccessType = () => { }, setOpenDialogSuccess, isReconnect = false,
        callbackAfterReconnect = () => { }, callbackClose = () => { },
    } = props;
    const router = useRouter();
    const [initialStep, setInitialStep] = useState(dataMp?.code?.toLowerCase()?.includes('tkpd') ? 0 : 1);
    const [step, setStep] = useState(initialStep);

    const [csConnectToChannel] = gqlService.csConnectToChannel();
    const [csReconnectStore] = gqlService.csReconnectStore();
    const [csCreateStoreRequest] = gqlService.csCreateStoreRequest();

    const { data, loading, error } = gqlService.csGetChannelCredential({
        variables: {
            channel_code: dataMp.code,
            callback_url: router.pathname,
            brand_id: dataMp.brandId,
        },
        skip: !dataMp.code || !dataMp.brandId || !!dataMp.connector,
    });

    const credentialsField = dataMp.connector ? connectorField : data?.csGetChannelCredential?.credentials || [];

    const initForm = () => {
        const values = {};
        const validation = {};

        const validationType = (type) => {
            switch (type) {
            case 'integer':
                return Yup.number().integer(t('sellersaleschannel:Value_must_be_an_integer'))
                    .typeError(t('sellersaleschannel:Value_must_be_a_number'));
            default:
                return Yup.string();
            }
        };

        credentialsField.forEach((field) => {
            values[field.name] = '';
            validation[field.name] = field.is_required ? validationType(field.type).required(t('sellersaleschannel:This_is_a_Required_field'))
                : validationType(field.type).nullable();
        });
        return { values, validation };
    };

    const formik = useFormik({
        initialValues: {
            ...initForm().values,
        },
        validationSchema: Yup.object().shape({
            ...initForm().validation,
        }),
        onSubmit: (values, { resetForm }) => {
            const cred = Object.keys(values);
            let variables = {};
            if (isReconnect) {
                variables = {
                    store_id: Number(dataMp.store_id),
                    credentials: cred.map((cr) => ({
                        field: cr,
                        type: credentialsField.find(({ name }) => name === cr)?.type || 'string',
                        value: values[cr],
                    })),
                };
                variables.credentials.push({ field: 'type', type: 'string', value: 'secret_key' });
            } else {
                variables = {
                    input: {
                        brand_id: dataMp.brandId,
                        channel_code: dataMp.code,
                        credentials: cred.map((cr) => ({
                            field: cr,
                            type: credentialsField.find(({ name }) => name === cr)?.type || 'string',
                            value: values[cr],
                        })),
                    },
                    framework: 'MPA',
                };
                variables.input.credentials.push({ field: 'type', type: 'string', value: 'secret_key' });
            }
            setOpenDialog(false);
            window.backdropLoader(true);
            const useMutation = isReconnect ? csReconnectStore : csConnectToChannel;
            useMutation({ variables })
                .then(() => {
                    resetForm();
                    setSuccessType('integration');
                    setStep(initialStep);
                    window.backdropLoader(false);
                    if (isReconnect) {
                        window.toastMessage({
                            open: true,
                            text: t('sellersaleschannel:Store_integration_has_been_connected'),
                            variant: 'success',
                        });
                        setTimeout(() => {
                            callbackAfterReconnect();
                        }, 250);
                    } else {
                        setOpenDialogSuccess(true);
                    }
                })
                .catch((e) => {
                    if (e?.graphQLErrors?.[0]?.extensions?.category === 'graphql-seller-authorization') {
                        router.push('/seller/unauthorized');
                    } else {
                        if (isReconnect) {
                            callbackAfterReconnect();
                        }
                        window.backdropLoader(false);
                        window.toastMessage({
                            open: true,
                            text: e.message,
                            variant: 'error',
                        });
                    }
                });
        },
    });

    const formikHelp = useFormik({
        initialValues: {
            shop_link: '',
        },
        validationSchema: Yup.object().shape({
            shop_link: Yup.string().required(t('sellersaleschannel:This_is_a_Required_field')),
        }),
        onSubmit: (values, { resetForm }) => {
            setOpenDialog(false);
            window.backdropLoader(true);
            csCreateStoreRequest({
                variables: {
                    input: {
                        ...values,
                        channel_code: dataMp.code,
                        ...(isReconnect && { store_id: Number(dataMp.store_id) }),
                    },
                },
            }).then((res) => {
                window.backdropLoader(false);
                if (res?.data?.csCreateStoreRequest) {
                    setSuccessType('access');
                    setStep(initialStep);
                    resetForm();
                    setOpenDialogSuccess(true);
                } else {
                    throw new Error(t('sellersaleschannel:Something_went_wrong_when_try_to_integrate_the_store'));
                }
            }).catch((e) => {
                setOpenDialog(true);
                window.backdropLoader(false);
                window.toastMessage({
                    open: true,
                    text: e.message,
                    variant: 'error',
                });
            });
        },
    });

    const onClose = () => {
        setOpenDialog(false);
        setStep(0);
        callbackClose();
    };

    useEffect(() => {
        const stepEffect = dataMp?.code?.toLowerCase()?.includes('tkpd') ? 0 : 1;
        setInitialStep(stepEffect);
        setStep(stepEffect);
    }, [dataMp.code, openDialog]);

    const contentProps = {
        ...props,
        formik,
        data: data?.csGetChannelCredential || {},
        credentialsField,
        loading,
        formikHelp,
        error,
        step,
        setStep,
        onClose,
        initialStep,
    };

    return (
        <View {...contentProps} />
    );
};

export default Core;
